/**
 * Data models for the NAV-Recon API surface.
 *
 * These mirror the JSON payloads returned by the `get_nav_recon_overview`
 * endpoint on the legacy Bottle back-end.
 */

/**
 * Status values emitted for each NAV-reconciliation alert check.
 *
 * The back-end currently only returns `Pass`, `Alert`, or `Fail`, but the
 * type is widened to `string` so future values do not break the type
 * checker. Use `specifyStatus` to normalise the value when rendering.
 */
export type NavReconAlertStatus = 'Pass' | 'Alert' | 'Fail' | string;

/** Per-asset-class weighted returns nested inside a NAV recon row. */
export interface NavReconAssetClassReturns {
  Alts?: number;
  Cash?: number;
  Commodity?: number;
  Equity?: number;
  'Fixed Income'?: number;
  Forex?: number;
  [assetClass: string]: number | undefined;
}

export interface NavReconShareClassCounts {
  base_currency?: number;
  non_base_unhedged?: number;
  non_base_hedged?: number;
}

export interface NavReconShareClassAverages {
  overall_mean?: number;
  base_currency_avg?: number;
  non_base_unhedged_avg?: number;
  non_base_hedged_avg?: number;
}

/**
 * Alert-status check results for a single fund.
 *
 * Each property represents a different comparison/validation the back-end
 * runs. For example `six_caceis_returns_status` compares the SIX-estimated
 * return against the return reported by CACEIS (the fund's administrator).
 */
export interface NavReconAlertStatuses {
  /** SIX-estimated return vs CACEIS-reported return. */
  six_caceis_returns_status?: NavReconAlertStatus;
  /** CACEIS-reported return vs Share Class price return. */
  caceis_share_price_returns_status?: NavReconAlertStatus;
  /** SIX-estimated return vs Share Class price return. */
  six_share_price_returns_status?: NavReconAlertStatus;
  /** Share Class price consistent with reported NAV. */
  sc_nav_px_returns_status?: NavReconAlertStatus;
  /** Share Class unit counts validation. */
  sc_unit_status?: NavReconAlertStatus;
}

export type NavReconClientAuditStatus =
  | 'approved'
  | 'rejected'
  | 'pending'
  | null;

/**
 * Processing lifecycle status for a risk data pipeline snapshot.
 *
 * Tracks ingestion/processing state. `system_error` denotes an internal
 * processing failure, not a data-quality issue or client rejection.
 */
export type NavReconRiskMetadataStatus =
  | 'loading'
  | 'system_error'
  | 'succeeded';

export const NAV_RECON_RISK_METADATA_STATUS_LABEL: Record<
  NavReconRiskMetadataStatus,
  string
> = {
  loading: 'Loading',
  system_error: 'Error',
  succeeded: 'Succeeded',
};

export const getNavReconRiskMetadataStatusLabel = (
  status: string | null | undefined,
): string => {
  if (!status) return 'Unknown';
  const normalized = status.toLowerCase() as NavReconRiskMetadataStatus;
  return NAV_RECON_RISK_METADATA_STATUS_LABEL[normalized] ?? status;
};

export type NavReconRiskDataName =
  | 'position'
  | 'nav_reconciliation'
  | 'nav_reconciliation_risk_monitor';

export interface NavReconPipelineSnapshot {
  client_audit_by: string | null;
  client_audit_by_name?: string | null;
  job_id: string | null;
  client_audit_timestamp: string | null;
  snapshot_timestamp: string;
  client_comment: string | null;
  description: string | null;
  /** Pipeline processing status (`loading` / `system_error` / `succeeded`). */
  status?: NavReconRiskMetadataStatus;
  /** Client audit action; null means implicitly approved until explicitly set. */
  client_audit_status: NavReconClientAuditStatus;
}

export interface NavReconPipelineStage {
  rs_data_name: NavReconRiskDataName;
  snapshots: NavReconPipelineSnapshot[];
}

export interface NavReconPipelineMetadata {
  fund_name: string;
  stages: NavReconPipelineStage[];
  position_date: string;
  requested_position_date: string;
}

export interface UpdateNavReconRiskDataMetaPayload {
  fund_name: string;
  position_date: string;
  rs_data_name: 'nav_reconciliation_risk_monitor';
  snapshot_timestamp: string;
  client_audit_status: 'approved' | 'rejected';
  /** Required for manual approve or reject (documents rationale). */
  client_comment: string;
}

/** Performance-split buckets nested inside overview `data`. Keys vary by fund. */
export interface NavReconPerformanceSplit {
  funding_swap_stocks?: number | null;
  certificate?: number | null;
  fees?: number | null;
  put_option?: number | null;
  clo_etf?: number | null;
  [component: string]: number | null | undefined;
}

/** Metrics inside `data` from `GET /get_nav_recon_overview/:positionDate`. */
export interface NavReconOverviewData {
  fund_currency?: string;
  fund_nav?: number;
  position_date?: string;
  snapshot_timestamp?: string;
  six_weighted_performance?: number;
  caceis_weighted_performance?: number;
  overall_sc_px_returns?: number;
  base_currency_market_performance?: number;
  bivariate_value?: number | null;
  sc_price_returns_std?: number;
  asset_class_returns?: NavReconAssetClassReturns;
  share_class_counts?: NavReconShareClassCounts;
  share_class_averages?: NavReconShareClassAverages;
  alert_statuses?: NavReconAlertStatuses;
  performance_split?: NavReconPerformanceSplit;
  new_exposures_count?: number;
  changed_exposures_count?: number;
}

/** One element of the array from `GET /get_nav_recon_overview/:positionDate`. */
export interface NavReconOverviewObject {
  fund_name: string;
  fund_id?: string;
  fund_name_full?: string;
  subfund_code?: string;
  /** `null` or `{}` when the back-end has no metrics yet for this fund (shell row). */
  data: NavReconOverviewData | null;
}

/** NAV / cash-flow inputs for one share class (NAV recon share-classes payload). */
export interface NavReconShareClassNavData {
  reds: number;
  fx_t_minus_1: number;
  subs: number;
  fx_t: number;
  nav_t_minus_1: number;
  nav_t: number;
}

/** Price inputs for one share class (NAV recon share-classes payload). */
export interface NavReconShareClassPriceData {
  dividend: number;
  fx_t_minus_1: number;
  fx_t: number;
  price_t_minus_1: number;
  price_t: number;
}

/** One share class row from `GET /get_nav_recon_share_classes/:date?fund_name=…`. */
export interface NavReconShareClass {
  share_class: string;
  units_change: number;
  price_return: number;
  nav_data: NavReconShareClassNavData;
  is_reconciled: boolean;
  /** Hedge classification; `null` when unknown / not applicable. */
  is_share_class_hedged?: boolean | null;
  units_at_t: number;
  nav_return: number;
  units_t_minus_1: number;
  price_data: NavReconShareClassPriceData;
}

/** One fund bucket in the share-classes API response (array element). */
export interface NavReconFundShareClasses {
  fund_name: string;
  fund_currency?: string;
  share_classes: NavReconShareClass[];
}

/** Asset-class keys in `positions_by_asset_class` / `asset_class_totals`. */
export type NavReconPositionAssetClass =
  | 'Forex'
  | 'Commodity'
  | 'Cash'
  | 'Equity'
  | 'Fixed Income'
  | 'Alts';

/** Per-asset-class or fund-level totals from `GET /get_nav_recon_positions/…`. */
export interface NavReconPositionTotals {
  six_caceis_gap_perc: number;
  caceis_p_and_l_perc: number;
  six_p_and_l_perc: number;
}

/** One holding line inside `positions_by_asset_class`. */
export interface NavReconPosition {
  six_price_t: number | null;
  caceis_market_value_t: number;
  six_caceis_gap_perc: number;
  base_currency_market_return_perc: number;
  name: string;
  fx_rate_t_minus_1: number;
  used_six_price: 'yes' | 'no';
  caceis_p_and_l: number;
  internal_asset_code: string | null;
  caceis_p_and_l_perc: number;
  six_p_and_l_perc: number;
  caceis_price_t_minus_1: number | null;
  caceis_price_t: number | null;
  position_size: number;
  position_price_change_perc_caceis: number;
  position_price_change_perc_six: number;
  position_size_change_perc: number;
  fx_cross: string;
  six_caceis_price_diff_perc: number | null;
  isin: string | null;
  base_currency: string;
  asset_type: string;
  fx_rate_t: number;
}

/** Alert/fail pair for a single NAV recon positions threshold type. */
export interface NavReconPositionThresholdPair {
  lower_threshold: number;
  upper_threshold: number;
}

/** Fund-level thresholds from `GET /get_nav_recon_positions/…`. */
export interface NavReconPositionThresholds {
  p_and_l_threshold: { fail: number; alert: number };
  /** Percentage-point threshold; positions exceeding this trigger an alert. */
  position_size_change_threshold: number;
  position_price_change_threshold: number;
  price_variation_threshold: {
    lower_threshold: number;
    upper_threshold: number;
  };
}

/** One fund bucket in the positions API response (array element). */
export interface NavReconFundPositions {
  fund_name: string;
  fund_currency: string;
  threshold: NavReconPositionThresholds;
  overall_totals: NavReconPositionTotals;
  asset_class_totals: Partial<
    Record<NavReconPositionAssetClass, NavReconPositionTotals>
  >;
  positions_by_asset_class: Partial<
    Record<NavReconPositionAssetClass, NavReconPosition[]>
  >;
  snapshot_timestamp: string;
}

/** Metrics inside `data` from `GET /get_nav_recon_overview2/:positionDate`. */
export interface NavReconOverviewExpandedData {
  fund_name?: string;
  min_sc_nav_return?: number;
  max_sc_nav_return?: number;
  mean_sc_nav_return?: number;
  min_sc_price_return?: number;
  max_sc_price_return?: number;
  mean_sc_price_return?: number;
  over_price_change_threshold?: boolean;
  est_return?: number;
  volatility?: number;
  total_net_assets?: number;
  snapshot_timestamp?: string;
  current_nav_date?: string;
  est_return_status?: NavReconAlertStatus;
  unit_return_status?: NavReconAlertStatus;
  share_class_price_deviation_status?: NavReconAlertStatus;
  /** Roll-up of per-metric statuses for the fund row. */
  aggregated_status?: NavReconAlertStatus;
  performance_split?: NavReconPerformanceSplit;
}

/** Pass / Alert / Fail fund counts from `GET /get_nav_recon_overview2/:positionDate`. */
export interface NavReconOverviewStatusCounts {
  Pass?: number;
  Alert?: number;
  Fail?: number;
  [status: string]: number | undefined;
}

/** Response envelope from `GET /get_nav_recon_overview2/:positionDate`. */
export interface NavReconOverview2Response {
  funds: NavReconOverviewExpandedObject[];
  overall_status: NavReconOverviewStatusCounts;
}

/** One element of the array from `GET /get_nav_recon_overview2/:positionDate`. */
export interface NavReconOverviewExpandedObject {
  fund_name: string;
  fund_id?: string;
  fund_name_full?: string;
  subfund_code?: string;
  /** `null` or `{}` when the back-end has no metrics yet for this fund (shell row). */
  data: NavReconOverviewExpandedData | null;
}
